import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Grid,
  TextInput,
  Select,
  Pagination,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconSearch,
  IconDownload,
  IconFileText,
  IconCalendar,
  IconExternalLink,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { publicTenderApi } from '../api/publicTenders';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';

interface Tender {
  id: string;
  description: string;
  category: 'GOODS' | 'SERVICES' | 'WORKS';
  stage: string;
  publishingDate: string;
  submissionDeadline: string;
  openingDate?: string;
  referenceNo: string;
  torSpecification?: string;
  torDocument?: {
    fileName: string;
    fileSize: number;
  };
}

export function PublicTenders() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Fetch published tenders
  const { data: tenders, isLoading, error } = useQuery({
    queryKey: ['public-tenders', searchTerm, selectedCategory, currentPage],
    queryFn: async () => {
      console.log('Making API call to fetch tenders...');
      try {
        const result = await publicTenderApi.getPublishedTenders({
          search: searchTerm || undefined,
          category: selectedCategory || undefined,
          page: currentPage,
          limit: 10,
        });
        console.log('API call successful:', result);
        return result;
      } catch (err) {
        console.error('API call failed:', err);
        throw err;
      }
    },
  });

  console.log('Component state:', { tenders, isLoading, error });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value || '');
    setCurrentPage(1);
  };

  const handleDownloadTor = async (tenderId: string) => {
    setDownloading(tenderId);
    try {
      await publicTenderApi.downloadTorDocument(tenderId);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(null);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'GOODS': return 'blue';
      case 'SERVICES': return 'green';
      case 'WORKS': return 'orange';
      default: return 'gray';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'PUBLISHED': return 'green';
      case 'COLLECTION': return 'blue';
      case 'OPENED': return 'yellow';
      case 'TECH_EVAL': return 'purple';
      case 'FIN_EVAL': return 'indigo';
      case 'AWARDED': return 'teal';
      default: return 'gray';
    }
  };

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Error">
          Failed to load tenders. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-300 to-emerald-400">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <Container size="lg" className="py-4">
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Logo height={48} />
              <Title order={1} size="h3" c="green.6">
                HMH Vendor Portal
              </Title>
            </Group>
            <Group gap="sm">
              <Button
                variant="subtle"
                onClick={() => navigate("/vendor/login")}
                color="green"
              >
                Sign In
              </Button>
              <Button onClick={() => navigate("/vendor/register")} color="green">
                Register Now
              </Button>
            </Group>
          </Group>
        </Container>
      </header>

      <Container size="lg" py="xl">
        <Stack gap="xl">
          {/* Page Header */}
          <div className="text-center">
            <div className="p-4 bg-green-50 rounded-full w-fit mx-auto mb-6">
              <IconFileText size={48} className="text-green-600" />
            </div>
            <Title order={1} size="h1" mb="md" c="green.7">
              Public Tender Opportunities
            </Title>
            <Text size="xl" c="dimmed" maw={600} mx="auto">
              View current tender opportunities and download TOR documents for HMH procurement projects.
            </Text>
          </div>

          {/* Filters */}
          <Card shadow="lg" padding="xl" radius="lg" withBorder className="bg-white/90 backdrop-blur-sm">
            <Stack gap="lg">
              <Title order={3} size="h4" c="green.7">
                Search & Filter
              </Title>
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <TextInput
                    placeholder="Search tenders..."
                    value={searchTerm}
                    onChange={(event) => handleSearch(event.currentTarget.value)}
                    leftSection={<IconSearch size={16} className="text-green-600" />}
                    size="md"
                    radius="md"
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Select
                    placeholder="Filter by category"
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    data={[
                      { value: '', label: 'All Categories' },
                      { value: 'GOODS', label: 'Goods' },
                      { value: 'SERVICES', label: 'Services' },
                      { value: 'WORKS', label: 'Works' },
                    ]}
                    clearable
                    size="md"
                    radius="md"
                  />
                </Grid.Col>
              </Grid>
            </Stack>
          </Card>

          {/* Results */}
          <div>
            {tenders?.data?.length === 0 ? (
              <Card shadow="lg" padding="xl" radius="lg" withBorder className="bg-white/90 backdrop-blur-sm">
                <Center py="xl">
                  <Stack gap="md" align="center">
                    <div className="p-4 bg-gray-50 rounded-full">
                      <IconFileText size={48} className="text-gray-400" />
                    </div>
                    <Title order={3} size="h3" c="gray.7">
                      No Tenders Found
                    </Title>
                    <Text c="dimmed" size="lg">
                      No published tenders match your search criteria.
                    </Text>
                  </Stack>
                </Center>
              </Card>
            ) : (
              <Stack gap="lg">
                {tenders?.data?.map((tender: Tender) => (
                  <Card key={tender.id} shadow="lg" padding="xl" radius="lg" withBorder className="bg-white/90 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
                    <Stack gap="lg">
                      <Group justify="space-between" align="flex-start">
                        <div style={{ flex: 1 }}>
                          <Title order={3} size="h3" mb="xs" c="green.7">
                            {tender.description}
                          </Title>
                          <Text c="dimmed" size="sm" mb="md" fw={500}>
                            Reference: {tender.referenceNo}
                          </Text>
                          <Group gap="lg">
                            <Group gap="xs">
                              <IconCalendar size={16} className="text-green-600" />
                              <Text size="sm" fw={500}>
                                Published: {tender.publishingDate 
                                  ? dayjs(tender.publishingDate).format('MMM DD, YYYY')
                                  : 'Not specified'
                                }
                              </Text>
                            </Group>
                            <Group gap="xs">
                              <IconCalendar size={16} className="text-teal-600" />
                              <Text size="sm" fw={500}>
                                Deadline: {tender.submissionDeadline 
                                  ? dayjs(tender.submissionDeadline).format('MMM DD, YYYY')
                                  : 'Not specified'
                                }
                              </Text>
                            </Group>
                          </Group>
                        </div>
                        <Group>
                          <Badge color={getCategoryColor(tender.category)} size="lg" radius="md">
                            {tender.category}
                          </Badge>
                          <Badge color={getStageColor(tender.stage)} size="lg" radius="md">
                            {tender.stage.replace('_', ' ')}
                          </Badge>
                        </Group>
                      </Group>
                      
                      <Group justify="space-between">
                        <Group>
                          {tender.torDocument && (
                            <Button
                              leftSection={<IconDownload size={16} />}
                              variant="outline"
                              size="md"
                              onClick={() => handleDownloadTor(tender.id)}
                              loading={downloading === tender.id}
                              color="green"
                              radius="md"
                            >
                              Download TOR
                            </Button>
                          )}
                          {tender.torSpecification && (
                            <Button
                              leftSection={<IconFileText size={16} />}
                              variant="outline"
                              size="md"
                              component={Link}
                              to={`/public/tenders/${tender.id}`}
                              color="teal"
                              radius="md"
                            >
                              View Details
                            </Button>
                          )}
                        </Group>
                        <Button
                          component={Link}
                          to={`/public/tenders/${tender.id}`}
                          rightSection={<IconExternalLink size={16} />}
                          color="green"
                          size="md"
                          radius="md"
                        >
                          View Full Details
                        </Button>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </div>

          {/* Pagination */}
          {tenders?.meta && tenders.meta.totalPages > 1 && (
            <Center>
              <Card shadow="sm" padding="md" radius="lg" withBorder className="bg-white/90 backdrop-blur-sm">
                <Pagination
                  total={tenders.meta.totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                  size="lg"
                  color="green"
                  radius="md"
                />
              </Card>
            </Center>
          )}
        </Stack>
      </Container>
    </div>
  );
}
